import React, { memo } from "react";

import { rowOpener } from "./DetailModal";
import { CATALOG_STATE, CATALOG_TEXT, FIX_KIND_LABELS, STATE_OPTIONS, shortWhen } from "./catalogText";

const T = CATALOG_TEXT.table;
const C = CATALOG_TEXT.card;
const COLUMNS = Object.freeze([T.when, T.category, T.rootCause, T.symptoms, T.fixKind, T.savedBy, T.state]);
const CELL = "px-4 py-3 align-middle";
const TAG = "inline-block rounded-full px-2 py-0.5 text-xs font-semibold";
const ROW = "border-t border-[#EBECE6] cursor-pointer transition hover:bg-[#fafaf8] focus:outline-none focus-visible:bg-cyan-50";
const stateLabel = (state) => (STATE_OPTIONS.find((o) => o.key === state) || { label: state }).label;

/** One manual entry as a row; a click, Enter or Space opens its details. Memoised: typing in a filter re-renders the page,
 * not the rows of the page already shown. */
const CatalogRow = memo(function CatalogRow({ item, onOpen }) {
  const undone = item.state === CATALOG_STATE.UNDONE;
  const symptoms = item.symptoms || [];
  return (
    <tr className={`${ROW} ${undone ? "text-gray-500" : "text-[#253439]"}`} aria-label={CATALOG_TEXT.detail.open(item.root_cause)}
      {...rowOpener(() => onOpen(item))}>
      <th scope="row" className={`${CELL} text-left font-medium whitespace-nowrap`}>{shortWhen(item.saved_at)}</th>
      <td className={`${CELL} text-xs uppercase tracking-wide text-cyan-700`}>
        {[item.product, item.category_label || item.category].filter(Boolean).join(" · ")}
      </td>
      <td className={CELL}><span className="block max-w-xs truncate" title={item.root_cause}>{item.root_cause}</span></td>
      <td className={CELL}>
        {symptoms.length
          ? <span className="block max-w-[16rem] truncate" title={symptoms.join("\n")}>{T.symptomSummary(symptoms.length, symptoms[0])}</span>
          : <span className="text-gray-400">{C.none}</span>}
      </td>
      <td className={CELL}>{item.fix_kind ? (FIX_KIND_LABELS[item.fix_kind] || item.fix_kind) : <span className="text-gray-400">{C.none}</span>}</td>
      <td className={`${CELL} whitespace-nowrap`}>{item.saved_by || C.unknownUser}</td>
      <td className={`${CELL} whitespace-nowrap`}>
        <span className={undone ? `${TAG} bg-gray-100 text-gray-600` : `${TAG} border border-cyan-200 bg-cyan-50 text-cyan-700`}>
          {stateLabel(item.state)}
        </span>
      </td>
    </tr>
  );
});

/** The Manual view's page of entries as a compact table, in the server's order (newest first). `loading` dims it while the
 * next page loads, so the previous page stays readable. */
export default function CatalogTable({ items, loading, onOpen }) {
  return (
    <div className={`overflow-x-auto rounded-xl border border-[#EBECE6] bg-white shadow-sm transition-opacity ${loading ? "opacity-60" : ""}`}>
      <table className="w-full border-collapse text-sm tabular-nums" aria-busy={loading}>
        <caption className="sr-only">{T.caption}</caption>
        <thead>
          <tr className="bg-[#f6f6f1] text-left text-gray-600">
            {COLUMNS.map((c) => <th key={c} scope="col" className="px-4 py-3 font-semibold whitespace-nowrap">{c}</th>)}
          </tr>
        </thead>
        <tbody>
          {items.map((item) => <CatalogRow key={item.id} item={item} onOpen={onOpen} />)}
        </tbody>
      </table>
    </div>
  );
}
